import { useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import { buildEndpoint } from '../EndPoints';
import { ServiceEvents } from '../shared/interfaces';

const useChatService = () => {
    const { doGet, doPost } = useFetch();
    
    // Estados de carga - Documentos
    const [fetchingGetDocuments, setFetchingGetDocuments] = useState(false);
    const [fetchingStoreDocument, setFetchingStoreDocument] = useState(false);
    const [fetchingShowDocument, setFetchingShowDocument] = useState(false);
    const [fetchingUpdateDocument, setFetchingUpdateDocument] = useState(false);
    const [fetchingDeleteDocument, setFetchingDeleteDocument] = useState(false);
    const [fetchingActiveDocument, setFetchingActiveDocument] = useState(false);
    const [fetchingSyncDocument, setFetchingSyncDocument] = useState(false);
    
    // Estados de carga - Chat
    const [fetchingSendMessage, setFetchingSendMessage] = useState(false);
    const [fetchingGetConversations, setFetchingGetConversations] = useState(false);
    const [fetchingGetConversation, setFetchingGetConversation] = useState(false);
    const [fetchingDeleteConversation, setFetchingDeleteConversation] = useState(false);
    
    const BASE_PATH = 'chat';
    
    // ==================== DOCUMENTOS ====================
    
    /**
     * Obtener listado de documentos del chatbot
     * @param filters - Filtros opcionales (search, category, sync_status, is_active)
     */
    const getDocuments = (filters: Record<string, any> = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingGetDocuments
        });
    };
    
    /**
     * Crear documento del chatbot
     * @param data - FormData con name, description, category, file
     */
    const storeDocument = (data: FormData, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents/store`),
            body: data,
            requestType: 'multipart',
            setFetching: setFetchingStoreDocument
        });
    };
    
    /**
     * Ver documento del chatbot
     */
    const showDocument = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents/${id}/show`),
            setFetching: setFetchingShowDocument
        });
    };
    
    /**
     * Actualizar documento del chatbot
     * @param data - FormData con name, description, category y opcionalmente file
     */
    const updateDocument = (id: number, data: FormData, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents/${id}/update`),
            body: data,
            requestType: 'multipart',
            setFetching: setFetchingUpdateDocument
        });
    };
    
    /**
     * Eliminar documento del chatbot
     */
    const deleteDocument = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents/${id}/delete`),
            body: {},
            setFetching: setFetchingDeleteDocument
        });
    };
    
    /**
     * Activar/Desactivar documento del chatbot
     */
    const activeDocument = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents/${id}/active`),
            body: {},
            setFetching: setFetchingActiveDocument
        });
    };
    
    /**
     * Re-sincronizar documento con OpenAI
     */
    const syncDocument = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/documents/${id}/sync`),
            body: {},
            setFetching: setFetchingSyncDocument
        });
    };
    
    // ==================== CHAT ====================
    
    /**
     * Enviar mensaje al chatbot
     * @param message - Mensaje del usuario
     * @param conversationId - ID de conversación existente (opcional)
     * @param document - Archivo opcional para análisis temporal (no se guarda en la base de conocimiento)
     */
    const sendMessage = (message: string, conversationId?: number, document?: File, events: ServiceEvents = {}) => {
        // Si hay documento, usar FormData
        if (document) {
            const formData = new FormData();
            formData.append('message', message);
            if (conversationId) {
                formData.append('conversation_id', conversationId.toString());
            }
            formData.append('document', document);
            
            doPost({
                ...events,
                url: buildEndpoint(`${BASE_PATH}/send-message`),
                body: formData,
                requestType: 'multipart',
                setFetching: setFetchingSendMessage
            });
        } else {
            doPost({
                ...events,
                url: buildEndpoint(`${BASE_PATH}/send-message`),
                body: {
                    message,
                    conversation_id: conversationId
                },
                setFetching: setFetchingSendMessage
            });
        }
    };
    
    /**
     * Obtener lista de conversaciones del usuario
     * @param filters - Filtros opcionales (search)
     */
    const getConversations = (filters: Record<string, any> = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingGetConversations
        });
    };
    
    /**
     * Obtener conversación con mensajes
     */
    const getConversation = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations/${id}`),
            setFetching: setFetchingGetConversation
        });
    };
    
    /**
     * Eliminar conversación
     */
    const deleteConversation = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/conversations/${id}/delete`),
            body: {},
            setFetching: setFetchingDeleteConversation
        });
    };
    
    return {
        // Métodos - Documentos
        getDocuments,
        storeDocument,
        showDocument,
        updateDocument,
        deleteDocument,
        activeDocument,
        syncDocument,
        // Métodos - Chat
        sendMessage,
        getConversations,
        getConversation,
        deleteConversation,
        // Estados de carga - Documentos
        fetchingGetDocuments,
        fetchingStoreDocument,
        fetchingShowDocument,
        fetchingUpdateDocument,
        fetchingDeleteDocument,
        fetchingActiveDocument,
        fetchingSyncDocument,
        // Estados de carga - Chat
        fetchingSendMessage,
        fetchingGetConversations,
        fetchingGetConversation,
        fetchingDeleteConversation
    };
};

export default useChatService;

